import React, { useEffect, useRef, useState } from "react";

import Logo from "assets/aligned-logo.svg";
import "./header.scss";
import { useHeaderData } from "hooks/queries/useHeaderData.query";
import { flatListToHierarchical } from "utils/navHelper.utils";
import MenuItem from "components/menu/menuItem";
import Hamburger from "components/hamburger/hamburger";
import classNames from "classnames";
import { useLocation } from "@reach/router";
import { TransLink } from "components/trans/transLink";
import useMobileViewport from "hooks/utils/useMobileViewport";

interface Props {
  stageLoaded: boolean;
}

const Header: React.FC<Props> = (props) => {
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [subOpen, setSubOpen] = useState<string | null>(null);
  const [itemActive, setItemActive] = useState<{id: string} | null>(null);
  const dummyHeightRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const headerMenuRef = useRef<HTMLUListElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { menu, meta, cta } = useHeaderData();
  const location = useLocation();
  const isMobileVP = useMobileViewport(1100);

  useEffect(() => {
    const handleScroll = () => {
      const height = dummyHeightRef.current?.clientHeight || 140;
      setCollapsed(window.scrollY >= height);
    };
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const main = mainRef.current;
    setMenuOpen(false);
    setSubOpen("");
    // get active item
    let itemsActive = menu.menuItems.nodes.filter((item: {path: string}) => location.pathname.match(item.path));
    if (!!itemsActive.length) {
      let itemActive = itemsActive[0]
      if (!!itemActive.parentId) {
        const itemsParent = menu.menuItems.nodes.filter((item: {id: string}) => item.id.match(itemActive.parentId));
        if (!!itemsParent.length) {
          itemActive = itemsParent[0];
        }
      }
      setItemActive(itemActive);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileVP) {
      if (menuOpen) {
        document.body.style.top = `-${window.scrollY}px`;
        document.body.style.position = "fixed";
      } else {
        const scrollY = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleResize = () => {
      const headerMenu = headerMenuRef.current;
      const wrapper = wrapperRef.current;
      if (!!headerMenu && !!wrapper) {
        const box = headerMenu.getBoundingClientRect();
        const boxWrapper = wrapper.getBoundingClientRect();
        headerMenu.style.setProperty('--header-x', `${Math.round(box.x - boxWrapper.x)}px`);
        headerMenu.style.setProperty('--header-width', `${Math.round(box.width)}px`);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [props.stageLoaded]);

  return (
    <header
      className={classNames("header", {
        "is-open": menuOpen,
        "is-collapsed": collapsed,
      })}
      ref={mainRef}
    >
      <div className="header-dummy__height" ref={dummyHeightRef} />
      {/* EXPAND */}
      <div
        className="header__expand"
        style={{
          height: subOpen
            ? `${
                document.getElementById(`${subOpen}-sub`)?.clientHeight || 0
              }px`
            : "0",
        }}
      />
      {/* MAIN */}
      <div className="header__main-container">
        <div className="header__main-wrapper" ref={wrapperRef}>
          {/* LOGO */}
          <div className="header-logo-bar">
            <TransLink to="/" className="header-logo">
              <Logo />
            </TransLink>
            <div className="header-hamburger">
              <Hamburger
                isOpen={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
              />
            </div>
          </div>
          {/* MENU */}
          <div className="header__menu-container">
            <ul className="header-menu" ref={headerMenuRef}>
              {flatListToHierarchical(menu.menuItems.nodes).map(
                (item: any, key: any) => {
                  return (
                    <MenuItem
                      {...item}
                      className="header-menu__item"
                      classNameSub="header-menu__sub"
                      key={key}
                      isActive = {!!subOpen && item.id === subOpen || !!itemActive && item.id === itemActive.id}
                      isSubOpen={item.id === subOpen}
                      activeItemId={!!itemActive && itemActive.id}
                      // onClick={(id) => setSubOpen(id)}
                      onClick={(id) => isMobileVP && setSubOpen(id)}
                      onHover={(id) => !isMobileVP && setSubOpen(id)}
                      onLeave={(id) => {
                        !isMobileVP && subOpen === id && setSubOpen("");
                      }}
                      onSubnavClose={() => setSubOpen("")}
                    />
                  );
                }
              )}
            </ul>
            {/* CTA */}
            <ul className="header-cta">
              {cta.menuItems.nodes.map((item: any, key: any) => (
                <MenuItem className="header-cta__item" {...item} key={key} />
              ))}
            </ul>
          </div>
        </div>
      </div>
      {/* META */}
      <div className="header-meta">
        <div className="header-meta__wrapper">
          <ul className="header-meta__list">
            {meta.menuItems.nodes.map((item: any, key: any) => (
              <MenuItem className="header-meta__item" {...item} key={key} />
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
